import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Package, MagnifyingGlass, Truck, PlayCircle, ClipboardText, CheckCircle, Barcode,
    ClipboardText as ClipboardIcon, NotePencil, ArrowsCounterClockwise, Crown,
    Printer, ArrowRight, Pause, Tooth, Stack, UserCircle, Lightning, Fire,
} from "@phosphor-icons/react";
import { StartManufacturingDialog, ExecutionLogDialog } from "@/components/case/ManufacturingDialogs";
import { PriorityChip } from "@/components/case/ProductionWorkflowStepper";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";

const STATUS_PILLS = {
    approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
    assigned_to_production: { label: "Assigned", cls: "bg-emerald-100 text-emerald-700" },
    submitted_to_production: { label: "Doctor-planned", cls: "bg-cyan-100 text-cyan-700" },
    in_production: { label: "In production", cls: "bg-blue-100 text-blue-700" },
    shipped: { label: "Shipped", cls: "bg-orange-100 text-orange-700" },
};

/* iter97.19 — Priority-specific SLA thresholds (mirrored from
   ProductionDashboard). Used to derive due-date chips on each row. */
const SLA_HOURS = { stat: 6, rush: 18, standard: 36 };

function hoursSince(iso) {
    if (!iso) return 0;
    return Math.max(0, (Date.now() - new Date(iso).getTime()) / 3600000);
}

function isSameDay(d, n = new Date()) {
    return d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
}

export default function ProductionQueue() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [cases, setCases] = useState([]);
    const [shipped, setShipped] = useState([]);
    const [onHold, setOnHold] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("queue"); // queue | shipped | on_hold
    const [priorityFilter, setPriorityFilter] = useState("all"); // all | rush | stat
    const [assignedToMe, setAssignedToMe] = useState(false);
    const [shipCase, setShipCase] = useState(null);
    /* iter98.99i — Premium "Case shipped" confirmation dialog. Captures
     * the just-shipped case code snapshot so the celebratory hero shows
     * the right value even after `load()` refreshes the queue list. */
    const [shippedDialog, setShippedDialog] = useState({ open: false, code: "" });
    const [startCase, setStartCase] = useState(null);
    const [logCase, setLogCase] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/production/queue"),
            api.get("/production/shipped"),
            api.get("/production/on-hold").catch(() => ({ data: [] })),
        ]).then(([q, s, h]) => {
            setCases(q.data);
            setShipped(s.data);
            setOnHold(h.data);
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* iter97.19 — "Assigned to me" pulls cases where the tech has
       acknowledged the case (becomes the de-facto fabricator) OR has been
       stamped as the explicit fabricator. */
    const isMine = (c) => {
        if (!user?.id) return false;
        return c.production_acknowledged_by === user.id
            || c.production_fabricator_user_id === user.id;
    };

    const source = activeTab === "queue" ? cases
        : activeTab === "shipped" ? shipped
        : onHold;
    const mineCount = useMemo(() => cases.filter(isMine).length, [cases, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const filtered = useMemo(() => {
        // Priority ordering: stat → rush → standard. Rush/STAT cases jump
        // to the front of the visible queue per the lab SOP golden rules.
        const priorityRank = { stat: 0, rush: 1, standard: 2 };
        let pool = [...source];
        // Optional priority filter (Layer 4) — narrows to rush+stat or just stat
        if (priorityFilter === "rush") {
            pool = pool.filter((c) => (c.production_priority || "standard") !== "standard");
        } else if (priorityFilter === "stat") {
            pool = pool.filter((c) => c.production_priority === "stat");
        }
        // iter97.19 — "Assigned to me" filter (queue tab only)
        if (assignedToMe && activeTab === "queue") {
            pool = pool.filter(isMine);
        }
        const ranked = pool.sort((a, b) => {
            const ra = priorityRank[a.production_priority || "standard"] ?? 2;
            const rb = priorityRank[b.production_priority || "standard"] ?? 2;
            return ra - rb;
        });
        if (!q) return ranked;
        const l = q.toLowerCase();
        return ranked.filter((c) =>
            (c.patient_name || "").toLowerCase().includes(l) ||
            (c.doctor_name || "").toLowerCase().includes(l) ||
            (c.batch_id || "").toLowerCase().includes(l) ||
            (c.tracking_number || "").toLowerCase().includes(l) ||
            (c.production_on_hold_reason || "").toLowerCase().includes(l)
        );
    }, [source, q, priorityFilter, assignedToMe, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    const start = (caseObj) => {
        /* Open the spec-aware Start dialog instead of firing the API
           directly — the tech must acknowledge the planner's spec before
           the case is claimed. */
        setStartCase(caseObj);
    };

    return (
        <div data-testid="production-queue" className="space-y-5 min-w-0">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Production queue</h1>
                    <p className="text-muted-foreground mt-1 text-sm">Manufacture approved cases, attach batch + tracking, mark shipped.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* iter97.19 — "Assigned to me" toggle. Lets the tech focus
                        on cases they've personally acknowledged. Hidden on
                        Shipped / On Hold tabs (filter doesn't apply there). */}
                    {activeTab === "queue" && (
                        <button
                            onClick={() => setAssignedToMe((v) => !v)}
                            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-semibold transition-colors ${
                                assignedToMe
                                    ? "bg-primary text-white border-primary"
                                    : "bg-card text-foreground border-border hover:border-primary/40 hover:text-primary"
                            }`}
                            data-testid="production-assigned-to-me-toggle"
                            title={assignedToMe ? "Showing only cases you've acknowledged" : "Show only cases assigned to you"}
                        >
                            <UserCircle size={13} weight={assignedToMe ? "fill" : "regular"} />
                            Mine
                            {mineCount > 0 && (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${assignedToMe ? "bg-white/20" : "bg-secondary"}`}>
                                    {mineCount}
                                </span>
                            )}
                        </button>
                    )}
                    {/* Priority filter chips — Layer 4. Clicking jumps the
                        view to only Rush/STAT cases. Rush includes STAT.
                        Active filter highlights solid; others stay outlined. */}
                    <div className="inline-flex bg-secondary rounded-full p-1" data-testid="production-priority-filter">
                        {[
                            { k: "all",  label: "All",  count: cases.length, Icon: null },
                            { k: "rush", label: "Rush+STAT", count: cases.filter((c) => (c.production_priority || "standard") !== "standard").length, Icon: Fire },
                            { k: "stat", label: "STAT",  count: cases.filter((c) => c.production_priority === "stat").length, Icon: Lightning },
                        ].map((f) => (
                            <button
                                key={f.k}
                                onClick={() => setPriorityFilter(f.k)}
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                                    priorityFilter === f.k
                                        ? f.k === "stat" ? "bg-rose-600 text-white"
                                            : f.k === "rush" ? "bg-amber-500 text-white"
                                            : "bg-white shadow text-foreground"
                                        : "text-muted-foreground hover:text-foreground"
                                }`}
                                data-testid={`production-priority-filter-${f.k}`}
                                disabled={f.k !== "all" && f.count === 0}
                                title={f.count === 0 && f.k !== "all" ? "No cases at this priority" : ""}
                            >
                                {f.Icon && <f.Icon size={11} weight="fill" />}
                                {f.label}
                                {f.count > 0 && <span className="ml-1 opacity-75">· {f.count}</span>}
                            </button>
                        ))}
                    </div>
                    <div className="relative w-full max-w-xs">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search patient, doctor, batch..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-9 h-10"
                            data-testid="production-search"
                        />
                    </div>
                </div>
            </div>

            {/* iter97.19 — Stat tiles wrapped in a SectionCard for visual
                parity with the dashboard. Same compact pattern across both
                pages reduces cognitive load when the tech bounces between
                them. */}
            <section
                className="rounded-2xl bg-[#F7F8FA] border border-[#E5E7EB]/80 p-4 sm:p-5"
                data-testid="production-queue-stats"
            >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                        { k: "approved", label: "Awaiting", val: cases.filter((c) => c.status === "approved" || c.status === "assigned_to_production" || c.status === "submitted_to_production").length, tone: "emerald", icon: ClipboardText },
                        { k: "in_production", label: "In production", val: cases.filter((c) => c.status === "in_production").length, tone: "primary", icon: Package },
                        { k: "on_hold", label: "On hold", val: onHold.length, tone: "rose", icon: Pause },
                        { k: "shipped", label: "Shipped", val: shipped.length, tone: "amber", icon: Truck },
                    ].map((s) => (
                        <StatTile key={s.k} stat={s} />
                    ))}
                </div>
            </section>

            <div className="inline-flex bg-secondary rounded-full p-1">
                {["queue", "shipped", "on_hold"].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        data-testid={`production-tab-${tab}`}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            activeTab === tab ? "bg-white text-foreground shadow" : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {tab === "queue" ? "Active queue"
                            : tab === "shipped" ? "Shipped"
                            : (
                                <span className="inline-flex items-center gap-1.5">
                                    On hold
                                    {onHold.length > 0 && (
                                        <span className="bg-rose-100 text-rose-800 text-[10px] font-bold px-1.5 rounded">{onHold.length}</span>
                                    )}
                                </span>
                            )}
                    </button>
                ))}
            </div>

            {loading && <div className="text-muted-foreground">Loading…</div>}
            {!loading && filtered.length === 0 && (
                <EmptyState
                    activeTab={activeTab}
                    assignedToMe={assignedToMe}
                    onClearMine={() => setAssignedToMe(false)}
                />
            )}

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {filtered.map((c) => {
                    const status = STATUS_PILLS[c.status] || STATUS_PILLS.approved;
                    /* Tiny aging cue — production has a 5-business-day informal
                       SLA from approved → shipped. We surface 'days in queue'
                       so the tech can triage older work first. Computed from
                       `status_updated_at`; falls back to `updated_at`. */
                    const ts = c.status_updated_at || c.updated_at;
                    const days = ts ? Math.floor((Date.now() - new Date(ts).getTime()) / 86400000) : null;
                    const ageCls = days == null ? ""
                        : days >= 5 ? "text-rose-700 bg-rose-50 border-rose-200"
                        : days >= 3 ? "text-amber-700 bg-amber-50 border-amber-200"
                        : "text-emerald-700 bg-emerald-50 border-emerald-200";

                    /* iter97.19 — Left-border priority accent. Lets the tech
                       scan the queue at a glance without reading the badge.
                       Rose for STAT, amber for Rush, slate for Standard. */
                    const p = c.production_priority || "standard";
                    const borderAccent =
                        p === "stat" ? "border-l-4 border-l-rose-600"
                        : p === "rush" ? "border-l-4 border-l-amber-500"
                        : "border-l-4 border-l-slate-200";

                    /* iter97.19 — Due-date chip. Same logic as the dashboard
                       Up Next row. Today/Overdue colour-coded. */
                    const threshold = SLA_HOURS[p] || SLA_HOURS.standard;
                    const dueIso = c.due_date || (ts ? new Date(new Date(ts).getTime() + threshold * 3600000).toISOString() : null);
                    const due = dueIso ? new Date(dueIso) : null;
                    const overdue = due ? due.getTime() < Date.now() : false;
                    const today = due ? isSameDay(due) : false;
                    const dueLabel = !due ? null : overdue ? "Overdue" : today ? "Today" : due.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
                    const dueCls = overdue ? "bg-rose-600 text-white"
                        : today ? "bg-amber-100 text-amber-800 border border-amber-200"
                        : "bg-slate-100 text-slate-700 border border-slate-200";

                    return (
                        <div
                            key={c.id}
                            className={`grid grid-cols-12 gap-3 px-5 py-4 border-b border-border last:border-b-0 items-center hover:bg-secondary/20 transition-colors ${borderAccent}`}
                            data-testid={`production-case-${c.id}`}
                        >
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/coe/production/case/${c.id}`)}
                                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/coe/production/case/${c.id}`); }}
                                className="col-span-12 md:col-span-4 min-w-0 cursor-pointer"
                                data-testid={`production-open-${c.id}`}
                            >
                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                    <span className="font-mono text-[10px] text-muted-foreground font-bold">{c.code || c.id?.slice(0, 8)}</span>
                                    <PriorityChip priority={c.production_priority} testId={`production-priority-${c.id}`} />
                                    {/* On-hold badge — Layer 3. Per spec Q7=c the case stays
                                        visible in the main queue while ALSO showing in the
                                        dedicated On Hold tab; the red chip distinguishes it. */}
                                    {c.production_on_hold && (
                                        <span
                                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200"
                                            title={c.production_on_hold_reason}
                                            data-testid={`production-on-hold-${c.id}`}
                                        >
                                            <Pause size={9} weight="fill" /> Hold · {c.production_on_hold_kind || "other"}
                                        </span>
                                    )}
                                    {/* Kind chip — refinement/retainer triage cue. Primary stays clean. */}
                                    {c.case_kind === "refinement" && (
                                        <span
                                            className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                            title={c.parent_case_code ? `Refinement of ${c.parent_case_code}` : "Refinement"}
                                            data-testid={`production-kind-${c.id}`}
                                        >
                                            <ArrowsCounterClockwise size={8} weight="fill" /> Refinement
                                        </span>
                                    )}
                                    {c.case_kind === "retainer" && (
                                        <span
                                            className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200"
                                            title={c.parent_case_code ? `Retainer for ${c.parent_case_code}` : "Retainer"}
                                            data-testid={`production-kind-${c.id}`}
                                        >
                                            <Crown size={8} weight="fill" /> Retainer
                                        </span>
                                    )}
                                </div>
                                <div className="font-medium text-sm truncate">{c.patient_name || "Patient"}</div>
                                <div className="text-xs text-muted-foreground truncate">{c.doctor_name} · {c.diagnosis}</div>
                                {/* iter97.15 + 97.19 — Aligner count + material spec chips.
                                    Lets the tech scan the queue without opening
                                    each case. Bumped from text-[10px] to text-[11px]
                                    + slightly heavier weight + bold border so they're
                                    visible from across the floor. Self-hides when
                                    data is missing. */}
                                {(c.plan?.aligner_count_upper != null || c.plan?.aligner_count_lower != null || c.manufacturing_spec?.sheet_name) && (
                                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                        {(c.plan?.aligner_count_upper != null || c.plan?.aligner_count_lower != null) && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20 font-bold tabular-nums"
                                                title="Upper / Lower aligner count"
                                                data-testid={`production-aligners-${c.id}`}
                                            >
                                                <Tooth size={11} weight="fill" />
                                                U{c.plan?.aligner_count_upper ?? 0} · L{c.plan?.aligner_count_lower ?? 0}
                                            </span>
                                        )}
                                        {c.manufacturing_spec?.sheet_name && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-cyan-50 text-cyan-800 border border-cyan-200 font-bold truncate max-w-[220px]"
                                                title={c.manufacturing_spec.sheet_name}
                                                data-testid={`production-spec-${c.id}`}
                                            >
                                                <Stack size={11} weight="fill" /> {c.manufacturing_spec.sheet_name}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-3 text-xs">
                                {c.batch_id && (
                                    <div className="flex items-center gap-1 mb-0.5"><Barcode size={11} /> {c.batch_id}</div>
                                )}
                                {c.tracking_number && (
                                    <div className="flex items-center gap-1 text-muted-foreground"><Truck size={11} /> {c.shipping_carrier || "Shipped"} · {c.tracking_number}</div>
                                )}
                                {!c.batch_id && !c.tracking_number && days != null && (
                                    <span
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded border font-bold uppercase tracking-wider text-[9px] ${ageCls}`}
                                        data-testid={`production-age-${c.id}`}
                                    >
                                        {days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"} in queue`}
                                    </span>
                                )}
                                {!c.batch_id && !c.tracking_number && days == null && <span className="text-muted-foreground italic">No batch yet</span>}
                            </div>
                            <div className="col-span-6 md:col-span-2 flex items-center gap-1.5 flex-wrap">
                                <span className={`text-[10px] px-2 py-1 rounded-full uppercase font-semibold ${status.cls}`}>{status.label}</span>
                                {/* iter97.19 — Due-date chip (only on active
                                    queue rows, not for shipped). */}
                                {dueLabel && activeTab !== "shipped" && (
                                    <span
                                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${dueCls}`}
                                        title={due ? `Due: ${due.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}` : ""}
                                        data-testid={`production-due-${c.id}`}
                                    >
                                        {dueLabel}
                                    </span>
                                )}
                            </div>
                            <div className="col-span-6 md:col-span-3 flex gap-2 md:justify-end flex-wrap">
                                {c.status === "assigned_to_production" && (
                                    <Button
                                        size="sm"
                                        onClick={() => navigate(`/coe/production/case/${c.id}`)}
                                        className="bg-amber-600 hover:bg-amber-700 text-white"
                                        data-testid={`production-review-${c.id}`}
                                        title="Review STL files & acknowledge before starting"
                                    >
                                        <CheckCircle size={14} className="mr-1.5" weight="fill" /> Review &amp; Acknowledge
                                    </Button>
                                )}
                                {(c.status === "approved" || c.status === "submitted_to_production") && (
                                    <Button size="sm" onClick={() => start(c)} data-testid={`production-start-${c.id}`}>
                                        <PlayCircle size={14} className="mr-1.5" /> Start
                                    </Button>
                                )}
                                {c.status === "in_production" && (
                                    <>
                                        <Button size="sm" variant="outline" onClick={() => setLogCase(c)} data-testid={`production-log-${c.id}`}>
                                            <NotePencil size={14} className="mr-1.5" /> Log
                                        </Button>
                                        <Button size="sm" onClick={() => setShipCase(c)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid={`production-ship-${c.id}`}>
                                            <Truck size={14} className="mr-1.5" /> Ship
                                        </Button>
                                    </>
                                )}
                                {c.status === "shipped" && (
                                    <>
                                        <a
                                            href={`${API}/cases/${c.id}/pdf/packing-slip?auth=${localStorage.getItem("ca_token")}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                                            title="Reprint the packing slip"
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`production-reprint-${c.id}`}
                                        >
                                            <Printer size={12} /> Slip
                                        </a>
                                        <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Out for delivery</span>
                                    </>
                                )}
                                <Link
                                    to={`/coe/production/case/${c.id}`}
                                    className="inline-flex items-center text-xs text-muted-foreground hover:text-primary"
                                    title="Open case detail"
                                    data-testid={`production-detail-${c.id}`}
                                >
                                    <ArrowRight size={14} />
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ShipDialog
                caseObj={shipCase}
                onClose={() => setShipCase(null)}
                onShipped={() => {
                    /* iter98.99i — Snapshot the case code before clearing
                     * shipCase, then fire the premium CaseSubmittedDialog. */
                    setShippedDialog({ open: true, code: shipCase?.code || "" });
                    setShipCase(null);
                    load();
                }}
            />

            <StartManufacturingDialog
                caseId={startCase?.id}
                open={!!startCase}
                onClose={() => setStartCase(null)}
                onStarted={() => { setStartCase(null); load(); }}
            />

            <ExecutionLogDialog
                caseId={logCase?.id}
                open={!!logCase}
                onClose={() => setLogCase(null)}
                onUpdated={() => load()}
                onCompleted={() => { setLogCase(null); load(); }}
            />

            {/* iter98.99i — Premium "Case shipped · on its way to the
                clinic" confirmation. Primary CTA simply dismisses (we're
                already on the queue page that just refreshed). */}
            <CaseSubmittedDialog
                open={shippedDialog.open}
                flow="case_shipped"
                caseCode={shippedDialog.code}
                primaryLabel="Done"
                onPrimary={() => setShippedDialog({ open: false, code: "" })}
            />
        </div>
    );
}

function ShipDialog({ caseObj, onClose, onShipped }) {
    const [batchId, setBatchId] = useState("");
    const [carrier, setCarrier] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (caseObj) {
            setBatchId(caseObj.batch_id || "");
            setCarrier(caseObj.shipping_carrier || "");
            setTrackingNumber(caseObj.tracking_number || "");
            setNote("");
        }
    }, [caseObj]);

    const submit = async () => {
        if (!caseObj) return;
        setBusy(true);
        try {
            await api.put(`/production/cases/${caseObj.id}/ship`, {
                batch_id: batchId, carrier, tracking_number: trackingNumber, note,
            });
            // iter98.99i — Toast suppressed; the parent fires the premium
            // CaseSubmittedDialog on shipped success.
            onShipped();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to ship");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!caseObj} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Mark case as shipped</DialogTitle>
                </DialogHeader>
                {caseObj && (
                    <div className="space-y-3 mt-2">
                        <p className="text-sm text-muted-foreground">{caseObj.patient_name} · {caseObj.doctor_name}</p>
                        <div>
                            <Label className="text-xs font-semibold">Batch ID</Label>
                            <Input value={batchId} onChange={(e) => setBatchId(e.target.value)} placeholder="e.g. BATCH-2026-0142" className="h-10 mt-1" data-testid="ship-batch-id" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Carrier</Label>
                            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Blue Dart, Delhivery..." className="h-10 mt-1" data-testid="ship-carrier" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Tracking number</Label>
                            <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className="h-10 mt-1" data-testid="ship-tracking" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Note (optional)</Label>
                            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1" data-testid="ship-note" />
                        </div>
                    </div>
                )}
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="ship-submit">
                        {busy ? "Shipping…" : "Confirm ship"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── iter97.19 — StatTile (tone-aware mini metric card) ─────────────────── */
function StatTile({ stat }) {
    const tones = {
        emerald: { border: "border-emerald-200", bg: "bg-emerald-50",  icon: "text-emerald-700",   num: "text-emerald-900" },
        primary: { border: "border-primary/20",  bg: "bg-primary/5",   icon: "text-primary",       num: "text-foreground" },
        rose:    { border: "border-rose-200",    bg: "bg-rose-50",     icon: "text-rose-700",      num: "text-rose-900" },
        amber:   { border: "border-amber-200",   bg: "bg-amber-50",    icon: "text-amber-700",     num: "text-amber-900" },
    };
    const t = tones[stat.tone] || tones.primary;
    const Icon = stat.icon;
    return (
        <div
            className={`rounded-xl border ${t.border} ${t.bg} p-3 shadow-sm`}
            data-testid={`production-stat-${stat.k}`}
        >
            <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={13} weight="fill" className={t.icon} />
                <div className="text-[9px] font-bold tracking-[0.16em] uppercase text-foreground/70">{stat.label}</div>
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums leading-none ${t.num}`}>{stat.val}</div>
        </div>
    );
}


/* ─── iter97.19 — EmptyState (per-tab copy + reset CTA) ──────────────────── */
function EmptyState({ activeTab, assignedToMe, onClearMine }) {
    const cfg = activeTab === "queue"
        ? {
            Icon: Package,
            title: assignedToMe ? "No cases assigned to you" : "Queue is empty",
            sub: assignedToMe
                ? "You haven't acknowledged any active cases. Pick one from the full queue to start fabrication."
                : "Approved cases will appear here automatically when the planner sends them through.",
        }
        : activeTab === "shipped"
            ? {
                Icon: Truck,
                title: "Nothing shipped yet",
                sub: "Once you confirm shipment from a case, it'll move here with the carrier + tracking link.",
            }
            : {
                Icon: Pause,
                title: "No cases on hold",
                sub: "Cases paused via the Hold action (equipment / material / file issue) will surface here.",
            };
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center" data-testid="production-queue-empty">
            <div className="w-14 h-14 mx-auto rounded-full bg-secondary/50 flex items-center justify-center mb-3">
                <cfg.Icon size={26} weight="duotone" className="text-muted-foreground" />
            </div>
            <div className="font-semibold text-foreground">{cfg.title}</div>
            <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">{cfg.sub}</div>
            {assignedToMe && activeTab === "queue" && (
                <button
                    onClick={onClearMine}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    data-testid="production-empty-clear-mine"
                >
                    Show full queue <ArrowRight size={11} />
                </button>
            )}
        </div>
    );
}
